import { IChingMethodTerm } from '../core/iching.models';

/** Stuk versregel: gewone tekst, of een begrip uit de methode. */
export interface IChingTextSegment {
  readonly text: string;
  readonly term?: IChingMethodTerm;
}

/** Woord uit een versregel (aanklikbaar Oordeel), met het begrip waar het in valt. */
export interface IChingWordToken {
  readonly word: string;
  readonly term?: IChingMethodTerm;
}

/** Eén regex voor alle begrippen plus een opzoektabel op de genormaliseerde vorm. */
export interface IChingTermMatcher {
  readonly pattern: RegExp;
  readonly byKey: ReadonlyMap<string, IChingMethodTerm>;
}

interface TermRange {
  readonly start: number;
  readonly end: number;
  readonly term: IChingMethodTerm;
}

/**
 * Bouwt de matcher: hele woorden (geen letter of cijfer ervoor of erna), hoofdletterongevoelig,
 * meerwoordige en langere begrippen eerst zodat "het edele" wint van "edele". Zonder begrippen:
 * `undefined`.
 */
export function buildIChingTermMatcher(terms: readonly IChingMethodTerm[]): IChingTermMatcher | undefined {
  const byKey = new Map<string, IChingMethodTerm>();
  for (const term of terms) {
    const key = termKey(term.term);
    if (key.length > 0 && !byKey.has(key)) {
      byKey.set(key, term);
    }
  }
  if (byKey.size === 0) {
    return undefined;
  }
  const alternatives = [...byKey.keys()]
    .sort((a, b) => wordCount(b) - wordCount(a) || b.length - a.length)
    .map(key => escapeRegExp(key).replace(/ /g, '\\s+'));
  return {
    pattern: new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives.join('|')})(?![\\p{L}\\p{N}])`, 'giu'),
    byKey,
  };
}

/** Begrippen die in de tekst voorkomen, in de volgorde van de methode. */
export function termsInText(text: string, terms: readonly IChingMethodTerm[], matcher: IChingTermMatcher | undefined): IChingMethodTerm[] {
  const found = new Set(findRanges(text, matcher).map(range => range.term));
  return terms.filter(term => found.has(term));
}

/** Versregel in stukken: tekst tussen begrippen en de begrippen zelf. */
export function segmentIChingTerms(text: string, matcher: IChingTermMatcher | undefined): IChingTextSegment[] {
  const ranges = findRanges(text, matcher);
  if (ranges.length === 0) {
    return [{ text }];
  }
  const segments: IChingTextSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start) });
    }
    segments.push({ text: text.slice(range.start, range.end), term: range.term });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor) });
  }
  return segments;
}

/** Versregel als woorden (split op witruimte); een woord binnen een begrip krijgt dat begrip mee. */
export function tokenizeIChingWords(text: string, matcher: IChingTermMatcher | undefined): IChingWordToken[] {
  const ranges = findRanges(text, matcher);
  const tokens: IChingWordToken[] = [];
  for (const match of text.matchAll(/\S+/g)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    const range = ranges.find(r => r.start < end && start < r.end);
    tokens.push(range === undefined ? { word: match[0] } : { word: match[0], term: range.term });
  }
  return tokens;
}

function findRanges(text: string, matcher: IChingTermMatcher | undefined): TermRange[] {
  if (matcher === undefined || text.length === 0) {
    return [];
  }
  const ranges: TermRange[] = [];
  for (const match of text.matchAll(matcher.pattern)) {
    const term = matcher.byKey.get(termKey(match[0]));
    if (term !== undefined) {
      const start = match.index ?? 0;
      ranges.push({ start, end: start + match[0].length, term });
    }
  }
  return ranges;
}

function termKey(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

function wordCount(key: string): number {
  return key.split(' ').length;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
